import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PortalHeader, PortalBody, StatTile, StatusPill, EmptyState } from "@/components/PortalUI";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PackagePlus } from "lucide-react";

export { ClientNewOrder as PartnerNewOrder } from "@/pages/client/ClientPages";

export function PartnerDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data));
  }, []);
  const byStatus = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});
  return (
    <>
      <PortalHeader eyebrow={`/ PARTNER · ${user?.org_name || ""}`} title="Partner control" testId="partner-dashboard-header">
        <Link to="/partner/orders/new" className="dt6-btn dt6-btn-primary" data-testid="partner-dashboard-new">
          <PackagePlus size={14} /> Submit client order
        </Link>
      </PortalHeader>
      <PortalBody>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatTile label="Total orders" value={orders.length} testId="partner-stat-total" />
          <StatTile label="In progress" value={byStatus.in_progress || byStatus.in_review || 0} testId="partner-stat-progress" />
          <StatTile label="Delivered" value={byStatus.delivered || 0} testId="partner-stat-delivered" />
          <StatTile label="This month" value={orders.filter(o => new Date(o.created_at) > new Date(Date.now() - 30*86400*1000)).length} testId="partner-stat-month" />
        </div>
        <h2 className="font-heading text-xl font-bold mb-3">Referred / managed orders</h2>
        {orders.length === 0 ? (
          <EmptyState testId="partner-orders-empty">
            NO ORDERS YET. SUBMIT ON BEHALF OF A CLIENT VIA <Link to="/partner/orders/new" className="dt6-link ml-1">NEW ORDER</Link>.
          </EmptyState>
        ) : (
          <div className="dt6-card overflow-hidden">
            <table className="dt6-table">
              <thead>
                <tr><th>Order</th><th>Module</th><th>Client</th><th>Status</th><th>Created</th></tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} data-testid={`partner-order-row-${o.id}`}>
                    <td className="font-mono text-xs">{o.id.slice(0,8).toUpperCase()}</td>
                    <td><span className="font-mono">{o.module_code}</span></td>
                    <td className="text-sm">{o.on_behalf_of_email || "—"}</td>
                    <td><StatusPill status={o.status} /></td>
                    <td className="font-mono text-xs text-[color:var(--dt6-muted)]">{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function PartnerBrand() {
  const [brand, setBrand] = useState({ display_name: "", accent_color: "#2E7CF6", logo_url: "" });
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/partner/brand").then((r) => setBrand({
      display_name: r.data?.display_name || "",
      accent_color: r.data?.accent_color || "#2E7CF6",
      logo_url: r.data?.logo_url || "",
    }));
  }, []);
  const submit = async (e) => {
    e.preventDefault(); setSaved(false); setErr("");
    try {
      await api.put("/partner/brand", brand);
      setSaved(true);
    } catch (e2) { setErr(formatApiError(e2)); }
  };
  return (
    <>
      <PortalHeader eyebrow="/ PARTNER" title="Brand settings" testId="partner-brand-header" />
      <PortalBody>
        <form onSubmit={submit} className="dt6-card p-6 max-w-2xl" data-testid="partner-brand-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="dt6-label">Display name</label>
              <input className="dt6-input" value={brand.display_name} onChange={(e)=>setBrand({...brand,display_name:e.target.value})} data-testid="partner-brand-name" />
            </div>
            <div>
              <label className="dt6-label">Accent color</label>
              <input className="dt6-input" value={brand.accent_color} onChange={(e)=>setBrand({...brand,accent_color:e.target.value})} data-testid="partner-brand-color" />
            </div>
            <div className="md:col-span-2">
              <label className="dt6-label">Logo URL</label>
              <input className="dt6-input" value={brand.logo_url} onChange={(e)=>setBrand({...brand,logo_url:e.target.value})} data-testid="partner-brand-logo" />
            </div>
          </div>
          <div className="mt-6 dt6-card p-4" style={{ background: "var(--dt6-bg)" }}>
            <div className="font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)] mb-2">/ LIVE PREVIEW</div>
            <div className="flex items-center gap-3">
              <div className="font-heading font-black text-2xl">
                {brand.display_name || "Partner brand"}
              </div>
              <span className="px-2 py-1 text-[0.7rem] font-mono tracking-[0.14em]" style={{ background: brand.accent_color, color: "#0B0E14" }}>ACCENT</span>
            </div>
          </div>
          {saved && <div className="mt-4 text-sm text-[color:var(--dt6-success)]" data-testid="partner-brand-saved">SAVED.</div>}
          {err && <div className="mt-4 text-sm text-[color:var(--dt6-danger)]" data-testid="partner-brand-error">{err}</div>}
          <div className="mt-6 flex justify-end"><button className="dt6-btn dt6-btn-primary" data-testid="partner-brand-submit">Save</button></div>
        </form>
      </PortalBody>
    </>
  );
}

export function PartnerWholesale() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/config/wholesale").then((r) => setRows(r.data));
  }, []);
  return (
    <>
      <PortalHeader eyebrow="/ PARTNER" title="Wholesale sheet" testId="partner-wholesale-header" />
      <PortalBody>
        <div className="dt6-card overflow-hidden">
          <table className="dt6-table" data-testid="partner-wholesale-table">
            <thead>
              <tr><th>Service</th><th>Module</th><th>Wholesale</th><th>Retail</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`wholesale-row-${r.id}`}>
                  <td>{r.service}</td>
                  <td><span className="font-mono">{r.module_code}</span></td>
                  <td className="font-mono">{r.wholesale_price}</td>
                  <td className="font-mono text-[color:var(--dt6-muted)]">{r.retail_price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PortalBody>
    </>
  );
}
